import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const HomePage: React.FC = () => {
  const { translations } = useLanguage();

  const quickNavItems = [
    { path: '/transport', label: translations.home.quickNav.transport, icon: '🚗', color: 'from-blue-400 to-cyan-400' },
    { path: '/stay', label: translations.home.quickNav.stay, icon: '🏠', color: 'from-green-400 to-emerald-400' },
    { path: '/tickets', label: translations.home.quickNav.tickets, icon: '🎫', color: 'from-purple-400 to-pink-400' },
    { path: '/cowork', label: translations.home.quickNav.cowork, icon: '💻', color: 'from-indigo-400 to-blue-400' },
    { path: '/culture', label: translations.home.quickNav.culture, icon: '🎨', color: 'from-orange-400 to-red-400' },
  ];

  const featuredActivities = [
    {
      id: 1,
      title: translations.home.featured.summerFestival,
      description: '體驗花蓮夏季的熱情與活力，參與在地節慶活動，感受原住民文化的魅力。',
      image: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&h=300&fit=crop',
      price: 'NT$ 800',
      gradient: 'from-yellow-400 to-orange-400'
    },
    {
      id: 2,
      title: translations.home.featured.whaleWatching,
      description: '在太平洋上尋找鯨魚的蹤跡，專業導覽員帶領，體驗海洋生態的奧秘。',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
      price: 'NT$ 1,200',
      gradient: 'from-blue-400 to-cyan-400'
    },
    {
      id: 3,
      title: translations.home.featured.hiking,
      description: '探索太魯閣峽谷的壯麗景色，專業嚮導帶領，安全體驗大自然的鬼斧神工。',
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=300&fit=crop',
      price: 'NT$ 600',
      gradient: 'from-green-400 to-emerald-400'
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Lighter Colors */}
      <section className="relative bg-gradient-to-br from-blue-100 via-indigo-50 to-cyan-100 text-gray-800 overflow-hidden">
        {/* Lighter Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200 opacity-30 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-32 w-24 h-24 bg-cyan-200 opacity-20 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-20 left-1/3 w-16 h-16 bg-indigo-200 opacity-25 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center slide-in-up">
            <div className="mb-6">
              <div className="inline-flex items-center px-6 py-3 bg-white/70 backdrop-blur-sm rounded-full text-sm font-medium mb-8 border border-white/30">
                <span className="mr-2">🌊</span>
                探索花蓮的自然美景與數位工作環境
              </div>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
              <span className="bg-gradient-to-r from-gray-800 via-blue-700 to-cyan-600 bg-clip-text text-transparent block">
                You Move
              </span>
              <span className="bg-gradient-to-r from-gray-800 via-blue-700 to-cyan-600 bg-clip-text text-transparent block">
                花蓮數位游牧
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed text-gray-700">
              {translations.home.hero.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/start"
                className="btn-animate bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-strong hover:shadow-xl transform hover:scale-105"
              >
                開始排行程
              </Link>
              <Link
                to="/tickets"
                className="btn-animate bg-white text-blue-600 px-8 py-4 rounded-2xl font-semibold text-lg hover:bg-gray-50 transition-all duration-300 shadow-strong hover:shadow-xl transform hover:scale-105 border border-blue-100"
              >
                {translations.home.hero.cta}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation with Enhanced Cards */}
      <section className="py-20 bg-white relative">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-blue-50/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient">
              {translations.home.quickNav.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              一鍵快速導覽，找到您需要的所有服務
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {quickNavItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className="group card-hover bg-white rounded-3xl p-8 text-center shadow-soft hover:shadow-strong border border-gray-100 hover:border-transparent overflow-hidden relative"
                style={{animationDelay: `${index * 0.1}s`}}
              >
                {/* Lighter Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                
                <div className="relative">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-hualien-blue transition-colors duration-300 text-lg">
                    {item.label}
                  </h3>
                </div>
                
                {/* Hover Effect */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-hualien-blue to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Activities with Enhanced Design */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient">
              {translations.home.featured.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              精選花蓮最受歡迎的體驗活動，讓您的旅程更加精彩
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredActivities.map((activity, index) => (
              <div
                key={activity.id}
                className="group card-hover bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-strong relative"
                style={{animationDelay: `${index * 0.2}s`}}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={activity.image}
                    alt={activity.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className={`absolute top-4 right-4 bg-gradient-to-r ${activity.gradient} text-white px-4 py-2 rounded-full text-sm font-semibold shadow-medium`}>
                    {activity.price}
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-white text-xl font-bold mb-2">{activity.title}</h3>
                  </div>
                </div>
                
                <div className="p-6">
                  <p className="text-gray-600 mb-6 leading-relaxed">{activity.description}</p>
                  
                  <div className="flex space-x-3">
                    <Link
                      to="/tickets"
                      className="flex-1 bg-gradient-to-r from-hualien-blue to-blue-500 text-white py-3 px-6 rounded-xl font-medium hover:from-blue-500 hover:to-blue-600 transition-all duration-300 text-center shadow-medium hover:shadow-strong"
                    >
                      {translations.tickets.bookNow}
                    </Link>
                    <button className="flex-1 bg-white border-2 border-hualien-blue text-hualien-blue py-3 px-6 rounded-xl font-medium hover:bg-hualien-blue hover:text-white transition-all duration-300 text-center">
                      查看詳情
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Why Hualien Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50/50 to-green-50/50 opacity-70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient">
              為什麼選擇花蓮？
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              花蓮擁有絕佳的自然環境、穩定的網路連線和友善的數位游牧社群，
              是您理想的遠端工作地點。在這裡，工作與生活完美平衡。
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: '🌊',
                title: '自然美景',
                description: '太平洋海景、太魯閣峽谷、清水斷崖等世界級景觀',
                gradient: 'from-blue-300 to-cyan-300'
              },
              {
                icon: '📶',
                title: '穩定網路',
                description: '高速光纖網路，多個共用工作空間提供專業環境',
                gradient: 'from-green-300 to-emerald-300'
              },
              {
                icon: '👥',
                title: '友善社群',
                description: '活躍的數位游牧社群，定期舉辦交流活動',
                gradient: 'from-purple-300 to-pink-300'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="group text-center p-8 rounded-3xl bg-white shadow-soft hover:shadow-strong transition-all duration-300 card-hover"
                style={{animationDelay: `${index * 0.2}s`}}
              >
                <div className={`w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-3xl group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section with Lighter Colors */}
      <section className="py-20 bg-gradient-to-br from-blue-100 via-indigo-50 to-cyan-100 text-gray-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-blue-200 opacity-30 rounded-full animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-16 h-16 bg-cyan-200 opacity-20 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800">
            準備好開始您的花蓮數位游牧之旅了嗎？
          </h2>
          <p className="text-xl mb-8 text-gray-700">
            立即探索我們的服務，找到最適合您的住宿、工作空間和活動
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/stay"
              className="btn-animate bg-white text-blue-600 px-8 py-4 rounded-2xl font-semibold text-lg hover:bg-gray-50 transition-all duration-300 shadow-strong hover:shadow-xl transform hover:scale-105 border border-blue-100"
            >
              尋找住宿
            </Link>
            <Link
              to="/cowork"
              className="btn-animate bg-white/60 backdrop-blur-sm px-8 py-4 rounded-2xl font-semibold text-lg hover:bg-white/80 transition-all duration-300 border border-white/40 text-gray-700"
            >
              探索工作空間
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 